/**
 * EoUcView — EO-вкладка «Юникоины» (screen-22, UC-панель — остаток паритета
 * E4; A26: staff-экономика юникоинов остаётся, EO-вид). Пять блоков:
 * Балансы (период УД2 + сортировка), Действия (бонус/штраф + обмен UC→₽,
 * УД12 «Отмена одна»), Леджер (тип-чипы + legacy УД11 + фильтр/«ещё» УД5),
 * Наклейки (EO неанонимно — УД7), Обмены (свои фильтры). Данные и мутации —
 * useUcAdmin (allSettled + ok-гарды); PTR + useAppReload; скелетон до первой
 * загрузки. Справки-«?» — TipBox с текстами эталона ДОСЛОВНО (js:235-249);
 * тап другого «?» переключает типс (паттерн EoConfigDicts).
 */
import { useState, type MouseEvent, type ReactElement } from "react";
import { PullHud } from "../../components/shared";
import { useAppReload, usePullToRefresh } from "../../hooks/usePullToRefresh";
import { useUcAdmin } from "../../hooks/useUcAdmin";
import { TipBox } from "./PriceRow";
import { UcActions } from "./UcActions";
import { UcBalances } from "./UcBalances";
import { UcExchanges } from "./UcExchanges";
import { UcLedger } from "./UcLedger";
import { UcStickers } from "./UcStickers";
import cfg from "./config.module.css";

type TipKey = "a" | "b" | "c" | "d" | "e";

/* Тексты справок — ДОСЛОВНО из эталона screen-22-uc.js:235-249. */
const TIPS: Record<TipKey, ReactElement> = {
  a: (
    <>
      <p><b>Юникоины</b> — внутренняя валюта персонала. Прирост считается за выбранный период.</p>
      <p><b>Бонус/штраф</b> — мгновенно в леджер; <b>обмен UC→₽</b> попадает в зарплату указанной датой.</p>
      <p><b>Потяните вниз</b> от верха — обновить данные.</p>
    </>
  ),
  b: (
    <>
      <p><b>Бонус/Штраф</b> — причина обязательна, операция сразу видна оператору в Telegram.</p>
      <p><b>Обмен</b> спишет UC и добавит ₽ в зарплату выбранной даты. Больше баланса списать нельзя.</p>
    </>
  ),
  c: (
    <>
      <p>Все движения UC. <b>Серые чипы</b> — legacy-операции старой системы, только для истории.</p>
      <p>Фильтр по оператору — чип «Все».</p>
    </>
  ),
  d: (
    <>
      <p>Наклейки операторов друг другу. EO видит отправителя — <b>анонимности здесь нет</b>.</p>
      <p>Справа — сколько наклейке осталось жить.</p>
    </>
  ),
  e: <p>История обменов UC→₽. Свои фильтры периода и оператора — селектор Балансов на них не влияет.</p>,
};

export function EoUcView() {
  const uc = useUcAdmin();
  const [tip, setTip] = useState<{ key: TipKey; anchor: HTMLElement } | null>(null);
  const reloadApp = useAppReload();
  const ptr = usePullToRefresh(() => Promise.all([uc.refetch(), reloadApp()]));

  /* Toggle: свой «?» закрывает, чужой — переключает (эталон showTip js:227). */
  const tipFor = (key: TipKey) => (e: MouseEvent<HTMLButtonElement>) => {
    const anchor = e.currentTarget;
    setTip((t) => (t && t.key === key ? null : { key, anchor }));
  };

  if (!uc.loaded) {
    return (
      <div className={cfg.scroll} ref={ptr.ref}>
        <PullHud pull={ptr.pull} pulling={ptr.pulling} refreshing={ptr.refreshing} />
        {uc.loadErr ? (
          <div className={cfg.errHint} style={{ margin: "8px 10px 0" }}>
            Юникоины не загрузились — потяните вниз, чтобы повторить.
          </div>
        ) : null}
        <div className={cfg.skel}>
          <div className={`${cfg.sk} ${cfg.skTitle}`} />
          {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => <div key={i} className={`${cfg.sk} ${cfg.skTea}`} />)}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={cfg.scroll} ref={ptr.ref}>
        <PullHud pull={ptr.pull} pulling={ptr.pulling} refreshing={ptr.refreshing} />
        {uc.loadErr ? (
          <div className={cfg.errHint} style={{ margin: "8px 8px 0" }}>
            Часть данных не загрузилась — потяните вниз, чтобы обновить.
          </div>
        ) : null}
        <UcBalances uc={uc} onTip={tipFor("a")} />
        <UcActions uc={uc} onTip={tipFor("b")} />
        <UcLedger uc={uc} onTip={tipFor("c")} />
        <UcStickers uc={uc} onTip={tipFor("d")} />
        <UcExchanges uc={uc} onTip={tipFor("e")} />
      </div>
      {tip ? <TipBox anchor={tip.anchor} onClose={() => setTip(null)}>{TIPS[tip.key]}</TipBox> : null}
    </>
  );
}
